import DropDownList from "@/components/Dropdown";
import { ListTypeTransaction, uuidNull } from "@/constants/default";
import { useGetDropDownTransactionTypesQuery } from "@/services/transactiontype.service";
import { useGetDropDownUnitCodesQuery } from "@/services/unitcode.service";
import _ from "lodash";
import { Dispatch, SetStateAction, useEffect } from "react";
import { FileUploader } from "react-drag-drop-files";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Image from "./_image";
import { TransactionImage } from "@/types/transactionimage.type";
import { EnumStatus } from "@/constants/enum";
import { PhotoProvider } from "react-photo-view";
import moment from "moment-timezone";
import { useGetDropDownBusinessUnitsQuery } from "@/services/businessunit.service";
import { TransactionFieldForm } from "@/types/transaction.type";
import { moneyRegex } from "@/utils/regex";

interface InformationProp {
    files: File[]
    setFiles: Dispatch<SetStateAction<File[]>>
    images?: TransactionImage[],
    setImages?: Dispatch<SetStateAction<TransactionImage[]>>
    isEdit?: boolean
}

const fileTypes = ["JPG", "PNG", "JPEG", "PDF"];

export default function Information({files, setFiles, images = [], setImages}: InformationProp) {
    const {t} = useTranslation()
    const {data: businessUnits, refetch: refetchBusinessUnit} = useGetDropDownBusinessUnitsQuery({status: `${EnumStatus.ACTIVE},${EnumStatus.INAVTIVE}`, checkExpired: false}, {refetchOnMountOrArgChange: true})
    const {data: unitcodes, refetch: refetchUnitCode} = useGetDropDownUnitCodesQuery(EnumStatus.ACTIVE, {refetchOnMountOrArgChange: true})
    const {data: transactionTypes, refetch: refetchTransactionType} = useGetDropDownTransactionTypesQuery(EnumStatus.ACTIVE, {refetchOnMountOrArgChange: true})
    const {setValue, control, register, formState: { errors }} = useFormContext<TransactionFieldForm>();
    const listType = ListTypeTransaction();
    const {fields, append, remove} = useFieldArray({
        control,
        name: "link_uploads",
        keyName: "key_link"
      })

    const handleChange = (data: File[]) => {
        setFiles([...files, ...data]);
    };

    useEffect(() => {
        refetchBusinessUnit()
        refetchUnitCode()
        refetchTransactionType()
    }, [])

    useEffect(() => {
        if(_.isArray(unitcodes)) {
            setValue("unitcode_id", unitcodes[0].value)
        }
    }, [unitcodes])

    useEffect(() => {
        if(_.isArray(transactionTypes)) {
            setValue("transactiontype_id", transactionTypes[0].value)
        }
    }, [transactionTypes])

    const onRemoveImageUpload = (index: number) => {
        const data = [...files];
        data.splice(index, 1)
        setFiles(data)
    }

    const onRemoveImage = (index: number) => {
        const data = [...images];
        data.splice(index, 1)
        setImages && setImages(data)
    }

    return (
        <div className="grid grid-cols-1 gap-9 sm:grid-cols-2 overflow-x-hidden overflow-y-auto px-6">
            <div>
                <div className="mb-3">
                    <label className="mb-1 block dark:text-white">
                        {t("transaction.business_unit")}
                    </label>
                    {
                        businessUnits && <Controller
                            defaultValue={uuidNull}
                            name="business_unit_id"
                            control={control}
                            rules={{required: t("validation.required")}}
                            render={({field: {onChange, value}}) => <DropDownList selected={value} setSelected={onChange} list={[{label: "None", value: uuidNull}, ...businessUnits]} />}
                        />
                    }
                    { errors.business_unit_id && <p className="mt-1 text-rose-600">{errors.business_unit_id.message}</p>}
                </div>
                <div className="mb-3">
                    <label className="mb-1 block dark:text-white">
                        {t("transaction.transaction_code")}
                    </label>
                    {
                        unitcodes && <Controller
                            name="unitcode_id"
                            control={control}
                            rules={{required: t("validation.required")}}
                            render={({field: {onChange, value}}) => <DropDownList selected={value} setSelected={onChange} list={unitcodes} />}
                        />
                    }
                    { errors.unitcode_id && <p className="mt-1 text-rose-600">{errors.unitcode_id.message}</p>}
                </div>
                <div className="mb-3">
                    <label className="mb-1 block dark:text-white">
                        {t("transaction.category")}
                    </label>
                    {
                        transactionTypes && <Controller
                            name="transactiontype_id"
                            control={control}
                            rules={{required: t("validation.required")}}
                            render={({field: {onChange, value}}) => <DropDownList selected={value} setSelected={onChange} list={transactionTypes} />}
                        />
                    }
                    { errors.transactiontype_id && <p className="mt-1 text-rose-600">{errors.transactiontype_id.message}</p>}
                </div>
                <div className="mb-3">
                    <label className="mb-1 block dark:text-white">
                        {t("transaction.type")}
                    </label>
                    <Controller
                        name="type"
                        control={control}
                        defaultValue={listType[0].value}
                        render={({field: {onChange, value}}) => <DropDownList selected={value} setSelected={onChange} list={listType} />}
                    />
                </div>
                <div className="mb-3">
                    <label className="mb-1 block dark:text-white">
                        {t("transaction.date")}
                    </label>
                    <input
                        type="date"
                        max={moment().format("YYYY-MM-DD")}
                        className="custom-input-date custom-input-date-1 w-full rounded border-[1.5px] border-stroke bg-transparent py-1.5 px-2 outline-none transition focus:border-primary active:border-primary dark:text-white dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary"
                        {...register("date", {required: t("validation.required")})}
                    />
                    { errors.date && <p className="mt-1 text-rose-600">{errors.date.message}</p>}
                </div>
            </div>
            <div>
                <div className="mb-3">
                    <label className="mb-1 block text-black dark:text-white">
                        {t("transaction.amount")}
                    </label>
                    <input
                        type="text"
                        placeholder={t("placeholder.amount")}
                        step="any"
                        className="w-full rounded border-[1.5px] border-stroke bg-transparent py-1.5 px-2 outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:text-white dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary"
                        {...register("amount", {
                            required: t("validation.required"),
                            pattern: {
                                value: moneyRegex,
                                message: t("validation.incorrect_money"),
                            }
                        })}
                    />
                    { errors.amount && <p className="mt-1 text-rose-600">{errors.amount.message}</p>}
                </div>
                <div className="mb-3">
                    <label className="mb-1 block text-black dark:text-white">
                        {t("form.description")}
                    </label>
                    <textarea
                        placeholder={t("placeholder.description")}
                        className="w-full rounded border-[1.5px] border-stroke bg-transparent py-1.5 px-2 outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:text-white dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary"
                        {...register("description")}
                    ></textarea>
                    { errors.description && <p className="mt-1 text-rose-600">{errors.description.message}</p>}
                </div>
                <div className="mb-3">
                    <label className="mb-1 block text-black dark:text-white">
                        {t("transaction.link_upload")}
                    </label>
                    {
                        fields.map((link, index) => (
                            <div className="flex mb-2" key={link.key_link}>
                                <input
                                    type="text"
                                    placeholder={t("placeholder.link_upload")}
                                    className="w-full rounded border-[1.5px] border-stroke bg-transparent py-1.5 px-2 outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:text-white dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary"
                                    {...register(`link_uploads.${index}.link`)}
                                />
                                {/* <div className="flex">
                                    <button type="button" className="rounded bg-primary ms-3 h-6 px-2 text-white ms-3 h-8 w-8 text-white hover:bg-opacity-90" onClick={() => append({link: ""})}>
                                        <i className="fa-light fa-plus" />
                                    </button>
                                    {fields.length > 1 && (
                                        <button type="button" className="rounded bg-danger ms-3 h-8 w-8 text-white hover:bg-opacity-90" onClick={() => remove(index)}>
                                            <i className="fa-sharp fa-light fa-trash-can" />
                                        </button>
                                    )}
                                </div> */}
                            </div>
                        ))
                    }
                </div>
                {/* <div className="mb-3">
                    <label className="mb-1 block dark:text-white capitalize">
                        {t("transaction.upload_your_file")}
                    </label>
                    <FileUploader
                        handleChange={handleChange}
                        name="file"
                        types={fileTypes}
                        multiple={true}
                        required={false}
                        maxSize={10} // 10 MB
                        children={<div>
                            <div className="relative mb-5.5 block w-full cursor-pointer appearance-none rounded border-2 border-dashed border-primary bg-gray-200 py-4 px-4 dark:bg-meta-4 sm:py-7.5">
                                <div className="flex flex-col items-center justify-center space-y-3">
                                    <span className="flex h-10 w-10 items-center justify-center rounded-full border border-stroke bg-white dark:border-strokedark dark:bg-boxdark">
                                        <i className="fa-regular fa-cloud-arrow-up text-primary" />
                                    </span>
                                    <p className="dark:text-white"><span className="text-primary">{t("transaction.click_upload")}</span> {t("transaction.drag_and_drop_your_file")}</p>
                                    <p className="mt-1.5 dark:text-white">PNG, JPG, PDF</p>
                                </div>
                            </div>
                        </div>}
                    />
                    <div className="grid grid-cols-4 gap-2 mt-3">
                        <PhotoProvider
                            toolbarRender={({ rotate, onRotate, onScale, scale }) => {
                                return (
                                    <>
                                        <i className="fa-sharp fa-light fa-circle-plus fa-lg mr-3 cursor-pointer text-white" onClick={() => onScale(scale + 1)} />
                                        <i className="fa-sharp fa-light fa-circle-minus fa-lg mr-3 cursor-pointer text-white" onClick={() => onScale(scale - 1)} />
                                        <i className="fa-light fa-rotate-right fa-lg cursor-pointer text-white" onClick={() => onRotate(rotate + 90)}/>
                                    </>
                                )
                            }}
                        >
                            {
                                images && images.map((image, index) => <Image key={image.id} index={index} dataImage={image} onRemove={() => onRemoveImage(index)} />)
                            }
                            {
                                Array.from(files).map((file, index) => <Image key={index} index={index} file={file} onRemove={() => onRemoveImageUpload(index)} />)
                            }
                        </PhotoProvider>
                        
                    </div>
                </div> */}
            </div>
        </div>
    )
}
